// Ekstraøvelse: imaginær skrivning — 3×10 korte ord, bogstav-divers, zen-UI.

import { MENTAL_IMAGERY_SUPPLEMENT } from '../data/mentalImageryWords';
import { filterWords, literal, makeRng, round } from '../data/curriculum/generators';
import type { Trin, UserProfile } from '../types';
import { cumulativeWordPool } from './cumulativeWordPool';
import { getCumulativeUnlockedKeys } from './dictationUnits';

export const MENTAL_IMAGERY_TRIN_ID = '__mental_imagery__';
export const MENTAL_IMAGERY_WORD_COUNT = 10;
export const MENTAL_IMAGERY_MAX_WORD_LENGTH = 4;
export const MENTAL_IMAGERY_MIN_WORD_LENGTH = 2;
export const MENTAL_IMAGERY_MIN_REPEATS = 1;
export const MENTAL_IMAGERY_MAX_REPEATS = 5;
/** Varighed ordet vises ved første forekomst i blink-runden. */
export const MENTAL_IMAGERY_FLASH_MS = 1000;

export function clampMentalImageryRepeats(value: number | undefined): number {
  const n = Math.round(Number(value) || 1);
  return Math.max(
    MENTAL_IMAGERY_MIN_REPEATS,
    Math.min(MENTAL_IMAGERY_MAX_REPEATS, n),
  );
}

/** Gentag hvert valgt ord N gange i rækkefølge (lad lad lad sal sal …). */
export function expandWordsForRepeats(
  words: string[],
  repeats: number,
): string[] {
  const count = clampMentalImageryRepeats(repeats);
  return words.flatMap((word) => Array.from({ length: count }, () => word));
}

export function fitsMentalImageryWord(word: string): boolean {
  return (
    word.length >= MENTAL_IMAGERY_MIN_WORD_LENGTH &&
    word.length <= MENTAL_IMAGERY_MAX_WORD_LENGTH
  );
}

/** Pulje: supplement (da/en) + pensum-ord — kun 2–4 bogstaver, inden for låste taster. */
export function mentalImageryWordPool(user: UserProfile): string[] {
  const allowed = getCumulativeUnlockedKeys(user);
  const seen = new Set<string>();
  const out: string[] = [];

  const add = (word: string) => {
    if (!fitsMentalImageryWord(word)) return;
    if (!filterWords([word], allowed).includes(word)) return;
    if (seen.has(word)) return;
    seen.add(word);
    out.push(word);
  };

  for (const word of MENTAL_IMAGERY_SUPPLEMENT) add(word);
  for (const word of cumulativeWordPool(user)) add(word);

  return out;
}

export function isMentalImageryEligible(user: UserProfile): boolean {
  return (
    user.completedTierIds.includes('niveau-1') &&
    mentalImageryWordPool(user).length >= MENTAL_IMAGERY_WORD_COUNT
  );
}

function uniqueLetters(word: string): Set<string> {
  const out = new Set<string>();
  for (const ch of word) {
    if (ch !== ' ') out.add(ch);
  }
  return out;
}

function countNewLetters(word: string, covered: ReadonlySet<string>): number {
  let n = 0;
  for (const ch of uniqueLetters(word)) {
    if (!covered.has(ch)) n += 1;
  }
  return n;
}

function diversityScore(word: string, covered: ReadonlySet<string>): number {
  const newLetters = countNewLetters(word, covered);
  return newLetters * 10 - word.length;
}

function shuffleInPlace<T>(items: T[], rng: () => number): void {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = items[i]!;
    items[i] = items[j]!;
    items[j] = tmp;
  }
}

/**
 * Vælg korte ord med bred bogstav-dækning. Prioriterer nye bogstaver og
 * foretrækker kortere ord ved lige dækning (bedre til ord-level mentalisering).
 */
export function pickLetterDiverseWords(
  pool: string[],
  count: number,
  seed?: number,
): string[] {
  const rng = makeRng(seed);
  const eligible = pool.filter(fitsMentalImageryWord);
  if (eligible.length === 0) return [];
  if (eligible.length <= count) {
    const out = [...eligible];
    shuffleInPlace(out, rng);
    return out.slice(0, count);
  }

  const remaining = new Set(eligible);
  const picked: string[] = [];
  const covered = new Set<string>();

  const starters = [...remaining].sort(
    (a, b) => diversityScore(b, covered) - diversityScore(a, covered),
  );
  const first = starters[Math.floor(rng() * Math.min(5, starters.length))]!;
  picked.push(first);
  remaining.delete(first);
  for (const ch of uniqueLetters(first)) covered.add(ch);

  while (picked.length < count && remaining.size > 0) {
    let best: string | null = null;
    let bestScore = -Infinity;
    let tieCount = 0;

    for (const candidate of remaining) {
      const score = diversityScore(candidate, covered);
      if (score > bestScore) {
        bestScore = score;
        best = candidate;
        tieCount = 1;
      } else if (score === bestScore) {
        tieCount += 1;
        if (rng() < 1 / tieCount) best = candidate;
      }
    }

    if (!best) break;
    picked.push(best);
    remaining.delete(best);
    for (const ch of uniqueLetters(best)) covered.add(ch);
  }

  while (picked.length < count && eligible.length > 0) {
    const rest = [...remaining];
    const word = rest[Math.floor(rng() * rest.length)]!;
    picked.push(word);
    remaining.delete(word);
  }

  shuffleInPlace(picked, rng);
  return picked;
}

export function buildMentalImageryTrin(
  user: UserProfile,
  options?: { repeats?: number },
): Trin | null {
  const pool = mentalImageryWordPool(user);
  if (pool.length < MENTAL_IMAGERY_WORD_COUNT) return null;

  const repeats = clampMentalImageryRepeats(
    options?.repeats ?? user.preferences?.mentalImageryRepeats,
  );
  const picked = pickLetterDiverseWords(pool, MENTAL_IMAGERY_WORD_COUNT);
  const sessionWords = expandWordsForRepeats(picked, repeats);
  const sessionText = sessionWords.join(' ');
  const unlockedKeys = getCumulativeUnlockedKeys(user);
  const repeatLabel =
    repeats === 1 ? 'én gang' : `${repeats} gange`;

  return {
    id: MENTAL_IMAGERY_TRIN_ID,
    tierId: 'grundniveau',
    trinNumber: 0,
    title: 'Imaginær skrivning',
    description: `${MENTAL_IMAGERY_WORD_COUNT} ord · ${repeatLabel} pr. runde — se, blink, lyt.`,
    goal: 'Forestil hele ordets fingerløb før du taster. Ingen tidspres.',
    unlockedKeys,
    newKeys: [],
    rounds: [
      round({
        id: `${MENTAL_IMAGERY_TRIN_ID}-r1`,
        title: 'Se og forestil',
        purpose:
          'Korte ord står stille. Forestil hele ordet — skriv når du er klar, og se hvert bogstav du taster.',
        mode: 'challenge',
        generate: literal(sessionText),
        presentation: { kind: 'imagery', phase: 'visible' },
      }),
      round({
        id: `${MENTAL_IMAGERY_TRIN_ID}-r2`,
        title: 'Kort blink',
        purpose:
          'Samme ord igen — et kort blink første gang, derefter prikker og skriv fra hukommelsen.',
        mode: 'challenge',
        generate: literal(sessionText),
        presentation: {
          kind: 'imagery',
          phase: 'flash',
          flashMs: MENTAL_IMAGERY_FLASH_MS,
        },
      }),
      round({
        id: `${MENTAL_IMAGERY_TRIN_ID}-r3`,
        title: 'Lyt indeni',
        purpose:
          'Lyt til hvert kort ord og skriv det uden at se det.',
        mode: 'challenge',
        generate: literal(sessionText),
        presentation: { kind: 'imagery', phase: 'dictation' },
      }),
    ],
  };
}
